import { AxiosInstance, AxiosError } from 'axios';
import { useEffect, useRef } from 'react';

// Import all API instances that need license denial handling
import { broadcastApi } from '../../api/broadcastAPI';
import { calendarApi } from '../../api/calendarAPI';
import { clientApi } from '../../api/clientAPI';
import { groupChatApi } from '../../api/groupChatAPI';
import { messageApi } from '../../api/messageAPI';
import { isViewAsReadOnly } from '../../api/sessionInterceptors';
import { subscriptionsApi } from '../../api/subscriptionsAPI';
import { templatesApi } from '../../api/templatesAPI';
import { usersApi } from '../../api/usersAPI';
import {
  useUpgradePrompt,
  isLicenseDenialError,
  isForbiddenError,
} from '../../context/UpgradePromptContext';

/**
 * Registry of API instances to attach interceptors to.
 * Add new API clients here as they are created.
 *
 * Note: Only APIs that export a shared instance can be included here.
 * APIs that create instances per-call (like telehealthAPI, dailyLogsAPI)
 * should use the useLicenseDenialInterceptor hook directly in their factory.
 */
const getApiInstances = (): AxiosInstance[] => {
  return [
    subscriptionsApi,
    clientApi,
    broadcastApi,
    groupChatApi,
    messageApi,
    calendarApi,
    usersApi,
    templatesApi,
  ];
};

/**
 * Component that sets up response interceptors on all API clients
 * to automatically handle 403 responses (both license denials and generic forbidden errors).
 *
 * This component should be rendered inside the UpgradePromptProvider
 * and will automatically attach interceptors to all registered API clients.
 *
 * The interceptors will:
 * 1. Check if a 403 response is a license denial (has `allowed: false`)
 * 2. If so, trigger the upgrade prompt modal
 * 3. For other 403 errors, show a generic access denied message (HA-217)
 * 4. Still reject the promise so components can handle the error
 *
 * Usage in App.tsx:
 * \`\`\`tsx
 * <UpgradePromptProvider>
 *   <LicenseDenialInterceptorSetup />
 *   {children}
 * </UpgradePromptProvider>
 * \`\`\`
 *
 * @returns null - This component doesn't render anything
 */
const LicenseDenialInterceptorSetup: React.FC = () => {
  const { handleLicenseDenial, handleForbiddenError } = useUpgradePrompt();
  const interceptorIdsRef = useRef<Map<AxiosInstance, number>>(new Map());

  useEffect(() => {
    const interceptorIds = interceptorIdsRef.current;
    const apiInstances = getApiInstances();

    // Add interceptors to all API instances
    apiInstances.forEach((apiInstance) => {
      const interceptorId = apiInstance.interceptors.response.use(
        // Success handler - pass through
        (response) => response,
        // Error handler - check for license denials and generic 403s
        (error: AxiosError) => {
          if (isLicenseDenialError(error)) {
            // Trigger upgrade prompt for license denials
            handleLicenseDenial(error.response.data);
          } else if (isForbiddenError(error) && !isViewAsReadOnly(error)) {
            // HA-447: a write refused in a support session gets the session banner's quiet
            // notice instead (see sessionInterceptors).
            // HA-217: Handle generic 403 errors (not license denials)
            // This prevents white screen crashes by showing a user-friendly error
            const errorMessage =
              typeof error.response?.data === 'object' &&
              error.response?.data !== null &&
              'message' in error.response.data
                ? String((error.response.data as { message: string }).message)
                : undefined;

            handleForbiddenError(errorMessage);
          }

          // Always reject so components can handle the error
          return Promise.reject(error);
        }
      );

      interceptorIds.set(apiInstance, interceptorId);
    });

    // Cleanup: remove all interceptors when component unmounts
    return () => {
      interceptorIds.forEach((interceptorId, apiInstance) => {
        apiInstance.interceptors.response.eject(interceptorId);
      });
      interceptorIds.clear();
    };
  }, [handleLicenseDenial, handleForbiddenError]);

  // This component doesn't render anything
  return null;
};

export default LicenseDenialInterceptorSetup;
